/* eslint-disable no-console */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { login } from '../service';
import { AuthQueryFactory } from '../utils/query-factory';

function useAuthCallback(
    tokenParameter: string | null,
    stateParameter: string | null,
    isImpersonating: boolean,
    complianceSystemSessionId: string | null,
    originalComplianceUserId: string | null,
    originalComplianceUserIdSignature: string | null,
    loginIdUsername: boolean,
    {
        onSuccess,
        onFailure,
    }: {
        onSuccess: (stateParameter: string | null) => void;
        onFailure: (error: Error) => void;
    }
) {
    const hasRunLogin = useRef(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        // Strict mode for React local development will run this hook twice
        // We only want to run this once
        if (hasRunLogin.current) return;

        hasRunLogin.current = true;

        if (tokenParameter === null) {
            onFailure(new Error('Failed to login'));
            return;
        }

        // Make login request to BFF
        login(
            tokenParameter,
            complianceSystemSessionId,
            isImpersonating,
            originalComplianceUserId,
            originalComplianceUserIdSignature,
            loginIdUsername
        )
            .then((loginUser) => {
                // Redirect to state parameter OR dashboard
                // Invalidate session as well
                void queryClient.invalidateQueries({
                    queryKey: AuthQueryFactory.session(),
                });

                // Pre-set the login data so we can skip an api call with useSession() later
                queryClient.setQueryData(AuthQueryFactory.session(), loginUser);

                if (stateParameter === null) {
                    onSuccess(null);
                    return;
                }

                onSuccess(decodeURIComponent(stateParameter));
            })
            .catch((ex) => {
                // Redirect to /auth/error
                console.error('Failed to login', ex);

                onFailure(ex as Error);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateParameter, tokenParameter, complianceSystemSessionId]);
}

export { useAuthCallback };
